import { AbstraxionProvider as AbsProvider, useAbstraxionAccount, useAbstraxionSigningClient, useModal as useAbstraxtionModal } from '@burnt-labs/abstraxion';
import { coin } from '@cosmjs/proto-signing';
import { useCallback, useEffect, useState } from 'react';
import { createContainer } from 'react-tracked';
import { dateMillis, deployed, id, TREASURY_ADDRESS, types } from '../constants';
import EventProvider, { useEvent } from '../pages/events/event/EventProvider';
import { useGlobalState } from '../state/GlobalStateProvider';
import { limitedApi } from '../utils';
import { useBackendApi } from './backendHelper';
import { cacheCall } from './cacheHelper';

const useCosmosHelper = () => {
  const [{ setIsLoading }] = useGlobalState();
  const { data: cosmosAccount, isConnected, isConnecting } = useAbstraxionAccount();
  const { client, logout, signArb } = useAbstraxionSigningClient();
  const [showAbstraxion, setShowAbstraxion] = useAbstraxtionModal();
  const [{ user, setUser }] = useGlobalState();
  const backendHelper = useBackendApi();
  const [{ campaign, token, communityName }, setState] = useEvent();
  const [state, setCosmosState] = useState({});
  const [cosmosLoggedIn, setIsLoggedIn] = useState(false);
  const [localUser, setLocalUser] = useState(user);
  const [xionBalance, setXionBalance] = useState();
  // const [profileLoading, setProfileLoading] = useState(false);

  const MESSAGE = 'Login to Mercle';

  async function getCosmosSign() {
    return '0xabcd1234abc';
  }

  const getXionNftProfileTokenId = async (communityName, ownerAddress, tokenAddress, chainId, refreshCache = false, isCosmos = false) => {
    const getNftTokenId = async () => {
      const token = await getXionTokenId(tokenAddress);
      return { tokenId: token };
    };

    const { tokenId, metadataUrl } = await cacheCall(id.storage.nftProfile(ownerAddress, tokenAddress, chainId), getNftTokenId, dateMillis.month_1, refreshCache);

    if (!tokenId) throw new Error('Nft Profile not found');

    return { tokenId, metadataUrl };
  };

  const refreshCosmosNftProfile = async (fetchTokenId = false) => {
    let tokenId = token?.tokenId;
    if (fetchTokenId) {
      const tokenDetails = await getXionNftProfileTokenId(communityName, user?.address, token.address, token.chainId, true, campaign?.isCosmos);
      tokenId = tokenDetails.tokenId;
      setState(s => ({ ...s, token: { ...s.token, tokenId } }));
    }

    return backendHelper.communities
      .getNftProfile(communityName, token.address, tokenId)
      .then(nftProfile => {
        setState(s => ({ ...s, nftProfile: nftProfile || null }));
      })
      .catch(e => {
        if (campaign?.isCosmos) {
          getXionNFTProfile(token.address, tokenId, client).then(profile => {
            if (profile) {
              setState(s => ({ ...s, nftProfile: profile }));
            } else {
              throw new Error('Could not get profile');
            }
          });
        } else {
          setState(s => ({ ...s, nftProfile: null }));
          throw new Error('Could not get profile');
        }
      });
  };

  const mintCosmosNFT = async (communityName, campaign, username, referralCode) => {
    const alertId = alert.loading('Verifying your details');

    try {
      window.mixpanel?.track?.('MintCard', {
        url: window.location.href,
        func: 'mintProfile',
        name: username,
      });

      alert.update(alertId, { render: 'Sculpting your Relic', isLoading: true });
      console.log(user || localUser);
      const offChainToOnChain = campaign.chainId === types.MercleOffchain && campaign?.onChainToken?.tokenAddr;
      const txnResponse = await backendHelper.communities.mintToken(
        communityName,
        campaign?.tokenAddr,
        campaign.name,
        user?.address || localUser?.address,
        username,
        referralCode,
        offChainToOnChain,
      );

      // await for txn

      alert.update(alertId, { render: 'Adding details', isLoading: true });
      const mintFee = txnResponse.mintClaim.message.fee;
      const chainId = offChainToOnChain ? campaign?.onChainToken?.chainId : campaign.chainId;

      const res = await client.execute(
        cosmosAccount?.bech32Address,
        deployed.MintWithClaim[chainId],
        {
          mint_with_claim: txnResponse?.mintClaim,
        },
        'auto',
        'test1',
        [coin(mintFee?.amount, mintFee?.denom)],
      );
      // alert.update(alertId, { render: 'Please wait while we mint your NFT', isLoading: true });

      alert.update(alertId, { render: 'Delivering Relic', isLoading: true });
      await refreshCosmosNftProfile(true);

      window.mixpanel?.track?.('MintCard', {
        url: window.location.href,
        func: 'mintProfile',
        name: username,
        success: true,
      });

      localStorage.removeItem(id.storage.referralCode);
      localStorage.removeItem(`cache_${id.storage.mintFeeTxnHash(campaign.commId)}`);
      alert.update(alertId, {
        render: 'Delivered',
        type: 'success',
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
      });
    } catch (error) {
      const alertConfig = {
        render: 'Error: Contact Support',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
      };

      if (error.message?.includes?.('user reject')) alertConfig.render = 'Transaction rejected';
      else if (error?.message?.includes?.('insufficient funds')) alertConfig.render = 'Insufficient Balance';
      else if (error?.message?.includes?.('estimateGas')) alertConfig.render = 'Gas estimation failed. Please ensure you have sufficient balance or try again after few seconds.';
      // else if (error?.message != '400') alertConfig.render = error?.message;
      else alertConfig.render = 'Something went wrong. Please try again after sometime';

      alert.update(alertId, alertConfig);
      console.error('error mintProfile', error);
      throw error;
    }
  };

  const getXionTokenId = useCallback(
    async tokenAddress => {
      try {
        const tokenId = await backendHelper.cosmos.getXionTokenId(tokenAddress, cosmosAccount?.bech32Address);
        return tokenId?.tokenId;
      } catch (error) {
        return null;
      }
    },
    [backendHelper, cosmosAccount],
  );

  const getXionNFTProfile = async (tokenAddress, tokenId, client) => {
    try {
      const nftProfile = await client?.queryContractSmart(tokenAddress, {
        nft_info: {
          token_id: `${tokenId}`,
        },
      });
      const nftJson = await fetch(nftProfile?.token_uri);
      return await nftJson.json();
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const isLoggedIn = async (callback) => {
    if(user) return callback();
    setShowAbstraxion(true)
  }

  const verifySign = useCallback(
    async (client, message, signature) => {
      const granteeAccountData = await client?.getGranteeAccountData();
      if (!granteeAccountData) return false;

      const userSessionAddress = granteeAccountData.address;
      const userSessionPubKey = Buffer.from(granteeAccountData.pubkey).toString('base64');
      const onError = window.location.href;
      const response = await backendHelper.getAccessTokenForWallet({
        signature,
        message,
        xionAddress: cosmosAccount?.bech32Address,
        userSessionPubKey,
        onError,
        userSessionAddress,
      });
      setUser(response);
      setIsLoading(false);
      setLocalUser(response);
      return response;
    },
    [client, backendHelper],
  );

  const getUserSignAndVerify = useCallback(
    async (paraClient = client, signMessage = signArb) => {
      try {
        if (paraClient && cosmosAccount?.bech32Address) {
          // Sign the message
          const response = await signMessage(paraClient?.granteeAddress, MESSAGE);
          await verifySign(paraClient, MESSAGE, response);
          setIsLoggedIn(true);
        }
      } catch (error) {
        console.log(error);
        alert.error('Something went wrong!');
        setIsLoggedIn(false);
      }
    },
    [alert, signArb, client, cosmosAccount, verifySign, isConnected],
  );

  // useEffect(() => {
  //   window.addEventListener('logout', cosmosLogout);
  // }, []);

  const cosmosLogout = useCallback(async () => {
    try {
      logout();
      setIsLoggedIn(false);
      backendHelper.logout().catch(e => console.error(e));
      localStorage.removeItem(id.storage.loginVia);
      setUser(null);
    } catch (error) {
      alert.error('Something went wrong!');
    }
  }, [logout, setIsLoggedIn]);

  const handleClose = useCallback(async () => {
    // await getUserSignAndVerify();
    setShowAbstraxion(false);
  }, [setShowAbstraxion]);

  const getAccountBalance = useCallback(async () => {
    if (!client) return null;
    try {
      const balance = await client?.getBalance(cosmosAccount?.bech32Address, 'uxion');
      return balance?.amount / 10 ** 6 || 0;
    } catch (e) {
      console.log('Error fetching balance', e);
    }
  }, [client, cosmosAccount]);

  useEffect(() => {
    if (!isConnected && !isConnecting) {
      setIsLoading(false);
    }
    if (isConnected && !user) {
      getUserSignAndVerify();
      // if (user?.xionAddress === cosmosAccount?.bech32Address && client) {
      //   setIsLoggedIn(true);
      // }
    }
    if (user) {
      setIsLoggedIn(true);
      setIsLoading(false);
    }
  }, [isConnected, client]);

  useEffect(() => {
    if (!user || !client || !cosmosLoggedIn) return;
    if (!token?.address) return;
    if (!campaign?.isCosmos) return;
    if (state?.nftProfile) return;

    getAccountBalance()
      .then(res => {
        if (res) {
          setXionBalance(res);
        } else {
          setXionBalance(0);
        }
      })
      .catch(error => {
        console.log('Error while fetching balance', error);
      });
    // setCosmosState(s=>({...s , profileLoading:true}))
    // setProfileLoading(true);

    limitedApi('nftProfile', () =>
      getXionNftProfileTokenId(communityName, user?.address, token.address, token.chainId, true, campaign?.isCosmos)
        .then(({ tokenId }) => {
          setState(s => ({ ...s, token: { ...s.token, tokenId } }));
          // setProfileLoading(true);
          backendHelper.communities
            .getNftProfile(communityName, token.address, tokenId)
            .then(nftProfile => {
              setState(s => ({ ...s, nftProfile: nftProfile || null }));

              return;
            })
            .catch(e => {
              setState(s => ({ ...s, nftProfile: null }));
                getXionNFTProfile(token?.address, tokenId, client).then(profile => {
                  if (profile) {
                    setState(s => ({ ...s, nftProfile: profile }));
                    return;
                  } else {
                    setState(s => ({ ...s, nftProfile: null }));
                    throw e;
                  }
                });
            });

          // alert.promise(promise, {
          //   pending: 'Loading your profile',
          //   success: 'Profile loaded',
          //   error: 'Could not get profile',
          // });
        })
        .catch(e => {
          setState(s => ({ ...s, nftProfile: null }));
          console.log(e);
        })
        .finally(() => {
          // setCosmosState(s=>({...s , profileLoading:false}))
        }),
    );
  }, [client, token, user]);

  const refreshBalance = async () => {
    try {
      const balance = await getAccountBalance();
      setXionBalance(balance);
    } catch (e) {
      setXionBalance(0);
    }
  };

  return [
    {
      cosmosLoggedIn,
      cosmosAccount,
      isConnected,
      isLoggedIn,
      getCosmosSign,
      mintCosmosNFT,
      getXionTokenId,
      cosmosLogout,
      getUserSignAndVerify,
      handleClose,
      getXionNFTProfile,
      ...state,
      refreshCosmosNftProfile,
      getAccountBalance,
      xionBalance,
      refreshBalance,
      isConnecting,
    },
    setCosmosState,
  ];
};

const { Provider, useTracked } = createContainer(() => useCosmosHelper());

export const useCosmos = useTracked;
export const CosmosProvider = ({ children, isSetting = false }) => {
  const treasuryConfig = {
    treasury: TREASURY_ADDRESS,
    rpcUrl: 'https://rpc.xion-mainnet-1.burnt.com:443',
    restUrl: 'https://api.xion-mainnet-1.burnt.com:443',
  };

  const [{ campaign }] = useEvent();
  if (campaign?.isCosmos || isSetting) {
    return (
      <AbsProvider config={treasuryConfig}>
        <Provider>{children}</Provider>
      </AbsProvider>
    );
  }
  return <>{children}</>;
};

export const CosmosEventProvider = ({ children }) => {
  return (
    <EventProvider>
      <CosmosProvider isSetting={true}>{children}</CosmosProvider>
    </EventProvider>
  );
};

export default CosmosProvider;
